/*
 *
 *  * Copyright (c) Funtip GbR 2017.
 *  * Simon Oldeboershuis, Perry Prust
 *
 *
 */

import ClientOAuth2 from 'client-oauth2';
import * as moment from 'moment';
import { call, delay, put } from 'redux-saga/effects';
import { AnyAction } from 'typescript-fsa';
import { handleFetchException, systemFinishFetching, systemStartFetching } from './components/System/actions';
import { setLoginRequired } from './components/System/system';
import { getSystemConfig } from './components/System/systemConfig';

export function isValidEmail(email: string) {
    const regexp = /^\S+@\S+\.\S+$/;
    return regexp.test(email);
}

export function addAccessTokenQueryParam(query: string, token: string) {
    if (!token) {
        return query;
    }
    if (query.indexOf('?') === -1) {
        // no query existing
        return query + '?access_token=' + token;
    } else {
        return query + '&access_token=' + token;
    }
}

export function logError(err: any, info?: React.ErrorInfo) {
    const compStack = info ? info.componentStack : '';
    // const stackTrace = StackTrace.getSync(err).map((sf: StackFrame) => sf.functionName + ':' + sf.lineNumber).join('; ');
    let errorMessage;
    if (err instanceof Error) {
        errorMessage = 'Error: ' + (err as Error).name + ', ' + err.message + ', ' + err.stack + ', ' + compStack;
    } else if (err instanceof Response) {
        errorMessage = 'Error-Response: ' + err.url + ', ' + err.status + ', ' + err.headers + ', ' + compStack;
    } else {
        errorMessage = `Error: ${JSON.stringify(err)}, ${compStack}`;
        console.info(err);
        console.info(compStack);
    }
    console.info(errorMessage);

    /*     const logInfo: LogInfo = {
        clientName: getSystemConfig().clientName,
        level: 'error',
        date: new Date(),
        message: errorMessage,
        attributes: {
            errJson: JSON.stringify(err),
            componentStack: compStack
        }
    };
    try {
        getLoggingApi().log(logInfo);
    } catch (err) {
        console.info(err);
    } */
}

// export type FetchCallBack<T> = (h: HttpHeaders) => Promise<T>;

export function* fetchWrapperFuncDelayed<T>(
    fetchCall: () => Promise<T>,
    successAction?: { action: (t: T) => AnyAction | undefined; delayInMillis?: number }[],
    errorAction?: () => AnyAction,
    loadingIndicatorAction?: ((loading: boolean) => AnyAction) | 'none'
) {
    let r: T | undefined = undefined;
    try {
        if (loadingIndicatorAction) {
            if (loadingIndicatorAction !== 'none') {
                yield put(loadingIndicatorAction(true));
            }
        } else {
            yield put(systemStartFetching());
        }
        // doing the fetch ...
        r = yield call(fetchCall);
        if (successAction) {
            for (const action of successAction) {
                if (action.delayInMillis) {
                    yield delay(action.delayInMillis);
                }
                if (r && action.action) {
                    const val = action.action(r);
                    if (val) {
                        yield put(val);
                    }
                }
            }
        }
    } catch (err) {
        if (errorAction) {
            yield put(errorAction());
        } else {
            logError(err);
            if (err instanceof Response) {
                yield put(handleFetchException(err));
            }
        }
    } finally {
        if (loadingIndicatorAction) {
            if (loadingIndicatorAction !== 'none') {
                yield put(loadingIndicatorAction(false));
            }
        } else {
            yield put(systemFinishFetching());
        }
    }
    return r;
}

export function* fetchWrapperFunc<T>(
    fetchCall: () => Promise<T>,
    successAction?: ((t: T) => AnyAction)[],
    errorAction?: () => AnyAction,
    loadingIndicatorAction?: ((loading: boolean) => AnyAction) | 'none'
) {
    const nonDelayedActions = successAction ? successAction.map((a) => ({ action: a })) : undefined;
    const r = yield* fetchWrapperFuncDelayed(fetchCall, nonDelayedActions, errorAction, loadingIndicatorAction);
    return r;
}

export function* fetchWrapperFuncNoLoading<T>(
    fetchCall: () => Promise<T>,
    successAction?: ((t: T) => AnyAction)[],
    errorAction?: () => AnyAction
) {
    return yield* fetchWrapperFunc(fetchCall, successAction, errorAction, 'none');
}

export function formatTimeDurationGerman(duration?: number) {
    if (duration) {
        return moment.duration(duration).locale('de').humanize();
    } else {
        return null;
    }
}

export interface OAuthState {
    nextUrl?: string;
}

export function redirectToLogin(nextUrl?: string) {
    const systemConfig = getSystemConfig();
    if (systemConfig.isUsePopupLogin()) {
        setLoginRequired(nextUrl || '');
    } else {
        const state: OAuthState = { nextUrl };
        let funtipOAuth = new ClientOAuth2({
            clientId: systemConfig.oauthClientId,
            clientSecret: '',
            accessTokenUri: systemConfig.oauthAuthenticationUri,
            authorizationUri: systemConfig.oauthAuthorizationUri,
            redirectUri: systemConfig.oauthRedirectUrl,
            scopes: systemConfig.oauthScopes,
            state: JSON.stringify(state),
        });
        window.location.href = funtipOAuth.token.getUri();
    }
}

export function formatNumber(n?: number, decimalDigits: number = 2) {
    if (n) {
        const nf = new Intl.NumberFormat(undefined, {
            style: 'decimal',
            minimumFractionDigits: decimalDigits,
            maximumFractionDigits: decimalDigits,
        });
        return nf.format(n);
    } else {
        return null;
    }
}

export function uniqueBy(a: any[], key: (x: any) => any) {
    return [...new Map(a.map((x) => [key(x), x])).values()];
}

export function isPushNotificationSupported(): boolean {
    return 'serviceWorker' in navigator && 'PushManager' in window;
}

export function isPushNotificationGranted(): boolean {
    return pushNotificationPermission() === 'granted';
}

export function pushNotificationPermission(): NotificationPermission | 'notAvailable' {
    if ('Notification' in window) {
        return window.Notification.permission;
    }
    return 'notAvailable';
}

export function registerServiceWorker(): Promise<ServiceWorkerRegistration> {
    if (isPushNotificationSupported()) {
        console.info('push supported!');
        return navigator.serviceWorker.register('/service-worker.js');
    } else {
        console.info('push not supported.');
        return Promise.reject('push service not supported.');
    }
}

export function askUserPermission(): Promise<NotificationPermission> {
    return Notification.requestPermission();
}

export function getPushSubscription(): Promise<PushSubscription | null> {
    if (!navigator.serviceWorker) {
        return Promise.reject('service worker not available.');
    }
    const serviceWorker: Promise<ServiceWorkerRegistration> = navigator.serviceWorker.ready;
    return serviceWorker.then((serviceworker) => serviceworker.pushManager.getSubscription());
}

export function getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration> {
    if (navigator.serviceWorker) {
        return navigator.serviceWorker.ready;
    } else {
        return Promise.reject('service worker not registered');
    }
}

export function unsubscribePushNotification() {
    console.info('about to unsubscribe ...');

    //wait for service worker installation to be ready
    const serviceWorker: Promise<ServiceWorkerRegistration> = getServiceWorkerRegistration();
    // subscribe and return the subscription

    serviceWorker.then((serviceworker) =>
        serviceworker.pushManager
            .getSubscription()
            .then((subscription) => {
                if (subscription) {
                    console.info('push notifications => unsubscribe');
                    subscription
                        .unsubscribe()
                        .then((success) => console.info('sucessfully unsubscribed '))
                        .catch((e) => console.warn('unsubscribe failed: ' + JSON.stringify(e)));
                } else {
                    console.info('no push notifications to unsubscribe');
                }
            })
            .catch((e) => console.warn('Error ' + JSON.stringify(e)))
    );
}

export function createNotificationSubscription(pushServerPublicKey: string): Promise<PushSubscription> {
    //wait for service worker installation to be ready
    const serviceWorker: Promise<ServiceWorkerRegistration> = navigator.serviceWorker.ready;
    // subscribe and return the subscription
    return serviceWorker.then((serviceworker) =>
        serviceworker.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(pushServerPublicKey),
        })
    );
}

export function initPushSubscriptions(pushServerPublicKey: string): Promise<PushSubscription> {
    return getServiceWorkerRegistration()
        .then((registration) =>  askUserPermission())
        .then((permission) => {
            console.info('PushNotification permission = '+ permission);
            if (permission === 'granted') {
                return createNotificationSubscription(pushServerPublicKey);
            } else {
                return Promise.reject('no permission granted from user');
            }
        })
        .catch((err) => {
            console.info(JSON.stringify(err));
            return Promise.reject(err);
        });
}

const urlBase64ToUint8Array = (base64String: string) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
};
